import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, ChevronLeft, Send, Plus, User, FileText, Settings, LogOut, Key, Menu, Users, Database, BookOpen } from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';

// Import original components
import { MessageContent } from './MessageContent';
import ChangePasswordModal from './ChangePasswordModal';
import KnowledgeModal from './KnowledgeModal';

const OptimizedChatInterface = () => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const eventSourceRef = useRef(null);
  const [isSidebarOpen, setSidebarOpen] = useState(() => {
    // 检查是否为移动设备
    return window.innerWidth >= 768;
  });
  const [intentTitle, setIntentTitle] = useState(null);
  const [conversations, setConversations] = useState([]);
  const finishFlagRef = useRef(false);
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, sessionId: null });
  const navigate = useNavigate();
  const [userName, setUserName] = useState('');
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const dropdownRef = useRef(null);
  const [isPasswordModalOpen, setPasswordModalOpen] = useState(false);
  const [isManager, setIsManager] = useState(false);
  const [isViewingHistory, setIsViewingHistory] = useState(false);
  const [activeLibrary, setActiveLibrary] = useState(1);

  // 更新资料库配置
  const [resourceLibraries, setResourceLibraries] = useState([]);

  // 更新推荐问题
  const trendingQuestions = [
    { text: '文旅集团的采购有哪些类型？' },
    { text: '采购需要经过谁审批？' },
    { text: '发起采购申请需要准备哪些材料？' },
    { text: '采购一项合同金额20万的项目，需要什么审批流程？' },
  ];

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  useEffect(() => {
    try {
      const storedConversations = JSON.parse(localStorage.getItem('DTC_CONVERSATIONS') || '[]');
      setConversations(storedConversations);
    } catch (error) {
      console.error('Error loading conversations:', error);
    }
  }, []);

  // Authentication check effect - keeping original functionality
  useEffect(() => {
    const checkAuthentication = async () => {
      try {
        const userToken = localStorage.getItem('DTC_USER_INFO');
        if (!userToken) {
          navigate('/login');
          return;
        }

        let userInfo;
        try {
          userInfo = JSON.parse(userToken);
          if (userInfo && userInfo.name) {
            setUserName(userInfo.name);
          }

          if (userInfo && userInfo.manager_code) {
            setIsManager(true);
          } else {
            setIsManager(false);
          }
        } catch (error) {
          console.error('Invalid JSON format for user info:', error);
          navigate('/login');
          return;
        }

        if (!userInfo ||
          !userInfo.code ||
          !userInfo.company_name ||
          !userInfo.token ||
          !userInfo.name) {
          navigate('/login');
          return;
        }

        const authHeader = `${userInfo.code}@${userInfo.token}`;

        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/user/heartbeat`, {
          headers: {
            'Authorization': authHeader
          }
        });

        const data = await response.json();

        if (data.result === 0) {
          toast.error('您的登录已过期，请重新登录。');

          setTimeout(() => {
            navigate('/login');
          }, 1500);
        } else if (data.result === 1) {
          // 更新本地存储中的 llm_id
          const userInfo = JSON.parse(localStorage.getItem('DTC_USER_INFO') || '{}');
          userInfo.llm_id = data.llm_id;
          localStorage.setItem('DTC_USER_INFO', JSON.stringify(userInfo));
        }
      } catch (error) {
        console.error('Authentication error:', error);
        navigate('/login');
      }
    };

    checkAuthentication();
  }, [navigate]);

  // Message encoding/decoding functions - keeping original functionality
  const encodeMessageData = (data) => {
    if (!data) return '';
    return btoa(encodeURIComponent(JSON.stringify(data)));
  };

  const decodeMessageData = (encodedData) => {
    if (!encodedData) return null;
    try {
      return JSON.parse(decodeURIComponent(atob(encodedData)));
    } catch (error) {
      console.error('Error decoding message data:', error);
      return null;
    }
  };

  // Save message to storage - keeping original functionality
  const saveMessageToStorage = (sessionId, message) => {
    if (!sessionId) return;

    const storageKey = `CONV_${sessionId}`;
    try {
      const existingMessages = JSON.parse(localStorage.getItem(storageKey) || '[]');

      const messageId = message.type === 'user'
        ? crypto.randomUUID() // Using modern API instead of uuidv4
        : message.request_id;

      if (existingMessages.some(msg => msg.id === messageId)) {
        return;
      }

      const messageToStore = {
        id: messageId,
        type: message.type,
        time: message.time,
        content: encodeMessageData(message.content),
        references: message.type === 'ai' ? encodeMessageData(message.references) : null,
        usage: message.type === 'ai' ? encodeMessageData(message.usage) : null
      };

      existingMessages.push(messageToStore);
      localStorage.setItem(storageKey, JSON.stringify(existingMessages));
    } catch (error) {
      console.error('Error saving message to storage:', error);
    }
  };

  // Send message handler - keeping original functionality
  const handleSend = async () => {
    if (!inputValue.trim() || isLoading) return;

    // Early user authentication check
    const userInfo = JSON.parse(localStorage.getItem('DTC_USER_INFO') || '{}');
    if (!userInfo.code || !userInfo.token) {
      toast.error('未找到有效的身份验证信息');
      navigate('/login');
      return;
    }
    const authToken = `${userInfo.code}@${userInfo.token}`;

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputValue,
      time: Date.now()
    };

    const aiMessage = {
      id: Date.now() + 1,
      type: 'ai',
      content: '',
      isStreaming: true,
      references: [],
      usage: null
    };

    setMessages(prev => [...prev, userMessage, aiMessage]);
    setInputValue('');
    setIsLoading(true);
    finishFlagRef.current = false;

    let newIntentTitle = "未明确的主题";

    try {
      // Only call intent API for new sessions
      if (!sessionId) {
        const intentUrl = `${process.env.REACT_APP_API_BASE_URL}/api/intent?prompt=${encodeURIComponent(inputValue)}&library_id=${activeLibrary || 1}`;
        try {
          const intentResponse = await fetch(intentUrl, {
            headers: {
              'Authorization': authToken
            }
          });

          const intentData = await intentResponse.json();
          newIntentTitle = intentData.title && intentData.title.trim()
            ? intentData.title
            : "未明确的主题";

          console.log('Intent Title:', newIntentTitle);
          setIntentTitle(newIntentTitle);
        } catch (intentError) {
          console.error('Error calling intent API:', intentError);
          setIntentTitle("未明确的主题");
        }
      }

      if (sessionId) {
        saveMessageToStorage(sessionId, userMessage);
      }

      // Proceed with normal chat API call
      const baseUrl = `${process.env.REACT_APP_API_BASE_URL}/api/chat?prompt=${encodeURIComponent(inputValue)}&library_id=${activeLibrary || 1}&llm_id=${userInfo.llm_id || 1}`;
      const url = `${baseUrl}${sessionId ? `&session_id=${sessionId}` : ''}&token=${encodeURIComponent(authToken)}`;

      eventSourceRef.current = new EventSource(url);

      eventSourceRef.current.onmessage = (event) => {
        try {
          console.log('Raw SSE event data:', event.data);

          const rawData = JSON.parse(event.data);

          // Handle error response
          if (rawData.content === "Error processing request") {
            setMessages(prev => prev.map(msg => {
              if (msg.id === aiMessage.id) {
                return {
                  ...msg,
                  content: '您好，请问我能帮您解答什么业务相关的问题吗？',
                  isStreaming: false
                };
              }
              return msg;
            }));
            eventSourceRef.current.close();
            setIsLoading(false);
            return;
          }

          const jsonData = JSON.parse(rawData.content.match(/data:(.+?)(?=\n|$)/)[1]);

          // If we get a new session_id and this is the first message
          if (!sessionId && jsonData.output && jsonData.output.session_id) {
            const newSessionId = jsonData.output.session_id;
            setSessionId(newSessionId);

            // Store conversation in local storage
            try {
              let conversations = JSON.parse(localStorage.getItem('DTC_CONVERSATIONS') || '[]');

              // Check if conversation with this session_id already exists
              const conversationExists = conversations.some(conv => conv.session_id === newSessionId);

              if (!conversationExists) {
                // Store the first user message
                saveMessageToStorage(newSessionId, userMessage);

                const updatedConversations = [
                  {
                    session_id: newSessionId,
                    intent_title: newIntentTitle
                  },
                  ...conversations
                ];
                localStorage.setItem('DTC_CONVERSATIONS', JSON.stringify(updatedConversations));
                setConversations(updatedConversations);
              }
            } catch (storageError) {
              console.error('Error handling local storage:', storageError);
            }
          }

          const { finish_reason, text, doc_references } = jsonData.output;
          const formattedText = typeof text === 'string' ? text.replace(/\\n\\n/g, '\n\n') : '';

          setMessages(prev => {
            return prev.map(msg => {
              if (msg.id === aiMessage.id) {
                const updatedMsg = {
                  ...msg,
                  content: msg.content + formattedText,
                };

                if (jsonData.usage?.models?.[0]) {
                  updatedMsg.usage = jsonData.usage.models[0];
                }

                // 处理推理过程的流式输出
                if (jsonData.output?.thoughts?.[0]?.thought) {
                  // 如果消息还没有 thoughts 属性，初始化它
                  if (!updatedMsg.thoughts) {
                    updatedMsg.thoughts = [{ thought: '' }];
                  }
                  // 获取新的推理内容
                  let newThought = jsonData.output.thoughts[0].thought;

                  // 如果是第一个字符，检查是否为中文或英文
                  if (!updatedMsg.thoughts[0].thought) {
                    const firstChar = newThought.charAt(0);
                    if (!/^[\u4e00-\u9fa5a-zA-Z]/.test(firstChar)) {
                      newThought = "好的，" + newThought.slice(1);
                    }
                  }

                  // 直接追加新的推理内容（流式输出每个事件都是增量内容）
                  updatedMsg.thoughts[0].thought += newThought;
                }

                if (finish_reason === 'stop' && !finishFlagRef.current) {
                  if (doc_references) {
                    updatedMsg.references = doc_references;
                  }
                }

                return updatedMsg;
              }
              return msg;
            });
          });

          if (finish_reason === 'stop' && !finishFlagRef.current) {
            finishFlagRef.current = true;

            setMessages(prev => {
              const updatedMessages = prev.map(msg => {
                if (msg.id === aiMessage.id) {
                  const finalMessage = {
                    ...msg,
                    isStreaming: false,
                    references: doc_references || [],
                    usage: jsonData.usage?.models?.[0] || null
                  };

                  const aiResponseToStore = {
                    type: 'ai',
                    time: Date.now(),
                    content: finalMessage.content + formattedText,
                    references: doc_references || [],
                    usage: jsonData.usage?.models?.[0] || null,
                    request_id: jsonData.request_id
                  };

                  const currentSessionId = jsonData.output.session_id;
                  saveMessageToStorage(currentSessionId, aiResponseToStore);

                  return finalMessage;
                }
                return msg;
              });
              return updatedMessages;
            });

            eventSourceRef.current.close();
            setIsLoading(false);
          }
        } catch (innerError) {
          console.error('Error parsing message data:', innerError);
          console.log('Problematic data:', event.data);
        }
      };

      eventSourceRef.current.onerror = (error) => {
        console.error('SSE Error:', error);
        eventSourceRef.current.close();
        setIsLoading(false);
      };

      eventSourceRef.current.addEventListener('complete', () => {
        eventSourceRef.current.close();
        setIsLoading(false);
      });

    } catch (error) {
      console.error('Error in chat:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: '发生错误，请刷新页面重试'
      }]);
      setIsLoading(false);
    }
  };

  // Conversation handlers - keeping original functionality
  const handleNewConversation = () => {
    setSessionId(null);
    setMessages([]);
    setIsViewingHistory(false);
  };

  const loadConversationHistory = async (selectedSessionId) => {
    setIsViewingHistory(true);
    if (!selectedSessionId) return;

    try {
      const storageKey = `CONV_${selectedSessionId}`;
      const encodedMessages = JSON.parse(localStorage.getItem(storageKey) || '[]');

      // 解码所有消息
      const allDecodedMessages = encodedMessages.map(msg => ({
        id: msg.id,
        type: msg.type,
        time: msg.time,
        content: decodeMessageData(msg.content),
        references: msg.type === 'ai' ? decodeMessageData(msg.references) : [],
        usage: msg.type === 'ai' ? decodeMessageData(msg.usage) : null,
        isStreaming: false
      }));

      // 对消息进行分组处理
      const processedMessages = [];
      let currentUserMessage = null;
      let aiVersions = [];

      for (let i = 0; i < allDecodedMessages.length; i++) {
        const msg = allDecodedMessages[i];

        if (msg.type === 'user') {
          // 如果有前一个用户消息和AI回答，先添加到处理后的消息列表
          if (currentUserMessage && aiVersions.length > 0) {
            processedMessages.push(currentUserMessage);

            // 添加最新的AI回答，并附带所有版本信息
            const latestAI = aiVersions[aiVersions.length - 1];
            processedMessages.push({
              ...latestAI,
              versions: aiVersions
            });

            // 重置AI版本数组
            aiVersions = [];
          } else if (currentUserMessage) {
            // 如果只有用户消息没有AI回答，直接添加
            processedMessages.push(currentUserMessage);
          }

          // 更新当前用户消息
          currentUserMessage = msg;
        } else if (msg.type === 'ai') {
          // 收集AI回答版本
          aiVersions.push(msg);
        }
      }

      // 处理最后一组消息
      if (currentUserMessage) {
        processedMessages.push(currentUserMessage);

        if (aiVersions.length > 0) {
          const latestAI = aiVersions[aiVersions.length - 1];
          processedMessages.push({
            ...latestAI,
            versions: aiVersions
          });
        }
      }

      setMessages(processedMessages);
    } catch (error) {
      console.error('Error loading conversation history:', error);
      setMessages([]);
    }
  };

  const handleSessionSelect = (selectedSessionId) => {
    setSessionId(selectedSessionId);
    loadConversationHistory(selectedSessionId);
  };

  const handleDeleteConversation = (e, sessionIdToDelete) => {
    e.stopPropagation();
    setDeleteDialog({ isOpen: true, sessionId: sessionIdToDelete });
  };

  const confirmDelete = () => {
    const sessionIdToDelete = deleteDialog.sessionId;
    try {
      // Remove from conversations list
      const updatedConversations = conversations.filter(
        conv => conv.session_id !== sessionIdToDelete
      );
      localStorage.setItem('DTC_CONVERSATIONS', JSON.stringify(updatedConversations));
      setConversations(updatedConversations);

      // Remove conversation messages
      localStorage.removeItem(`CONV_${sessionIdToDelete}`);

      // If currently viewing the deleted conversation, clear it
      if (sessionId === sessionIdToDelete) {
        setSessionId(null);
        setMessages([]);
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
    setDeleteDialog({ isOpen: false, sessionId: null });
  };

  // User profile dropdown handlers
  const handleMouseEnter = () => {
    setDropdownVisible(true);
  };

  const handleMouseLeave = (e) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.relatedTarget)) {
      setDropdownVisible(false);
    }
  };

  const handleChangePassword = () => {
    setPasswordModalOpen(true);
    setDropdownVisible(false);
  };

  const handleLogout = () => {
    toast.success('已成功退出登录。');
    localStorage.removeItem('DTC_USER_INFO');

    setTimeout(() => {
      navigate('/login');
    }, 1500);
  };

  // Function to handle trending question click
  const handleTrendingQuestionClick = (question) => {
    setInputValue(question.text);
  };

  // 添加响应式处理
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 在组件初始化时设置资料库访问权限
  useEffect(() => {
    const userToken = localStorage.getItem('DTC_USER_INFO');
    let userType = 1; // 默认为1

    if (userToken) {
      try {
        const userInfo = JSON.parse(userToken);
        userType = userInfo.type || 1;
      } catch (error) {
        console.error('Error parsing user info:', error);
      }
    }

    // 根据用户类型设置资料库访问权限
    const libraries = [
      {
        id: 1,
        title: '集团制度库',
        icon: '📚',
        enabled: userType === 1 || userType === 3
      },
      {
        id: 2,
        title: '上级政策库',
        icon: '📋',
        enabled: userType === 2 || userType === 3
      }
    ];

    setResourceLibraries(libraries);

    // 如果当前选中的库不可访问，自动切换到第一个可访问的库
    const firstEnabledLibrary = libraries.find(lib => lib.enabled);
    if (firstEnabledLibrary && !libraries.find(lib => lib.id === activeLibrary)?.enabled) {
      setActiveLibrary(firstEnabledLibrary.id);
    }
  }, []); // 仅在组件挂载时执行一次

  const [isModelModalOpen, setModelModalOpen] = useState(false);
  const [isKnowledgeModalOpen, setKnowledgeModalOpen] = useState(false);
  const [currentModel, setCurrentModel] = useState(() => {
    return localStorage.getItem('DTC_SELECTED_MODEL') || 'default';
  });

  const [tempSelectedModel, setTempSelectedModel] = useState(null);

  // 修改handleModelChange为临时选择
  const handleModelSelect = (modelId) => {
    setTempSelectedModel(modelId);
  };

  // 修改确认切换模型的处理函数
  const handleConfirmModelChange = async () => {
    if (tempSelectedModel) {
      try {
        const userInfo = JSON.parse(localStorage.getItem('DTC_USER_INFO'));
        const authHeader = `${userInfo.code}@${userInfo.token}`;

        // 根据选择的模型确定 llm_id
        const llm_id = tempSelectedModel === 'default' ? 1 : 2;

        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/stat/llm`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': authHeader
          },
          body: JSON.stringify({ llm_id })
        });

        const data = await response.json();

        if (data.success) {
          // 更新本地存储中的 llm_id
          userInfo.llm_id = llm_id;
          localStorage.setItem('DTC_USER_INFO', JSON.stringify(userInfo));

          setCurrentModel(tempSelectedModel);
          setModelModalOpen(false);
          toast.success('模型切换成功');
        } else {
          toast.error(data.message || '模型切换失败');
        }
      } catch (error) {
        console.error('Error updating model:', error);
        toast.error('模型切换失败，请稍后重试');
      }
    }
  };

  const handleModelModalOpen = () => {
    // 从本地存储获取用户信息
    const userInfo = JSON.parse(localStorage.getItem('DTC_USER_INFO') || '{}');
    // 获取 llm_id，如果没有则默认为 1
    const llm_id = userInfo.llm_id || 1;

    // 根据 llm_id 设置默认选中的模型
    const defaultModel = llm_id === 1 ? 'default' : 'r1-32b';
    setTempSelectedModel(defaultModel);
    setModelModalOpen(true);
  };

  // 添加重新生成回答的函数
  const handleRegenerate = async (messageIndex) => {
    // 找到最后一条用户消息
    let lastUserMessageIndex = -1;
    for (let i = messageIndex - 1; i >= 0; i--) {
      if (messages[i].type === 'user') {
        lastUserMessageIndex = i;
        break;
      }
    }

    if (lastUserMessageIndex === -1) return;

    const userMessage = messages[lastUserMessageIndex];
    const userPrompt = userMessage.content;

    // 更新当前AI消息为"思考中"状态
    setMessages(prev => {
      const updatedMessages = [...prev];
      updatedMessages[messageIndex] = {
        ...updatedMessages[messageIndex],
        content: '',
        isStreaming: true,
        thoughts: null, // 重置推理过程
        references: [],
        usage: null
      };
      return updatedMessages;
    });

    setIsLoading(true);
    finishFlagRef.current = false;

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    try {
      const userInfo = JSON.parse(localStorage.getItem('DTC_USER_INFO') || '{}');
      if (!userInfo.code || !userInfo.token) {
        toast.error('未找到有效的身份验证信息');
        navigate('/login');
        return;
      }
      const authToken = `${userInfo.code}@${userInfo.token}`;

      // 调用聊天API获取新回答
      const baseUrl = `${process.env.REACT_APP_API_BASE_URL}/api/chat?prompt=${encodeURIComponent(userPrompt)}&library_id=${activeLibrary || 1}&llm_id=${userInfo.llm_id || 1}`;
      const url = `${baseUrl}${sessionId ? `&session_id=${sessionId}` : ''}&token=${encodeURIComponent(authToken)}`;

      eventSourceRef.current = new EventSource(url);

      eventSourceRef.current.onmessage = (event) => {
        try {
          console.log('Raw SSE event data:', event.data);

          const rawData = JSON.parse(event.data);

          // Handle error response
          if (rawData.content === "Error processing request") {
            setMessages(prev => {
              const updatedMessages = [...prev];
              updatedMessages[messageIndex] = {
                ...updatedMessages[messageIndex],
                content: '您好，请问我能帮您解答什么业务相关的问题吗？',
                isStreaming: false
              };
              return updatedMessages;
            });
            eventSourceRef.current.close();
            setIsLoading(false);
            return;
          }

          const jsonData = JSON.parse(rawData.content.match(/data:(.+?)(?=\n|$)/)[1]);
          const { finish_reason, text, doc_references } = jsonData.output;
          const formattedText = typeof text === 'string' ? text.replace(/\\n\\n/g, '\n\n') : '';

          setMessages(prev => {
            const updatedMessages = [...prev];
            const currentMessage = updatedMessages[messageIndex];

            const updatedMsg = {
              ...currentMessage,
              content: currentMessage.content + formattedText,
            };

            if (jsonData.usage?.models?.[0]) {
              updatedMsg.usage = jsonData.usage.models[0];
            }

            // 处理推理过程的流式输出
            if (jsonData.output?.thoughts?.[0]?.thought) {
              // 如果消息还没有 thoughts 属性，初始化它
              if (!updatedMsg.thoughts) {
                updatedMsg.thoughts = [{ thought: '' }];
              }
              // 获取新的推理内容
              let newThought = jsonData.output.thoughts[0].thought;

              // 如果是第一个字符，检查是否为中文或英文
              if (!updatedMsg.thoughts[0].thought) {
                const firstChar = newThought.charAt(0);
                if (!/^[\u4e00-\u9fa5a-zA-Z]/.test(firstChar)) {
                  newThought = "好的，" + newThought.slice(1);
                }
              }

              // 直接追加新的推理内容（流式输出每个事件都是增量内容）
              updatedMsg.thoughts[0].thought += newThought;
            }

            if (finish_reason === 'stop' && !finishFlagRef.current) {
              if (doc_references) {
                updatedMsg.references = doc_references;
              }
            }

            updatedMessages[messageIndex] = updatedMsg;
            return updatedMessages;
          });

          if (finish_reason === 'stop' && !finishFlagRef.current) {
            finishFlagRef.current = true;

            setMessages(prev => {
              const updatedMessages = [...prev];
              const currentMessage = updatedMessages[messageIndex];

              const finalMessage = {
                ...currentMessage,
                isStreaming: false,
                references: doc_references || [],
                usage: jsonData.usage?.models?.[0] || null
              };

              const aiResponseToStore = {
                type: 'ai',
                time: Date.now(),
                content: finalMessage.content + formattedText,
                references: doc_references || [],
                usage: jsonData.usage?.models?.[0] || null,
                request_id: jsonData.request_id
              };

              const currentSessionId = jsonData.output.session_id;
              saveMessageToStorage(currentSessionId, aiResponseToStore);

              updatedMessages[messageIndex] = finalMessage;
              return updatedMessages;
            });

            eventSourceRef.current.close();
            setIsLoading(false);
          }
        } catch (innerError) {
          console.error('Error parsing message data:', innerError);
          console.log('Problematic data:', event.data);
        }
      };

      eventSourceRef.current.onerror = (error) => {
        console.error('SSE Error:', error);
        eventSourceRef.current.close();
        setIsLoading(false);
      };

      eventSourceRef.current.addEventListener('complete', () => {
        eventSourceRef.current.close();
        setIsLoading(false);
      });

    } catch (error) {
      console.error('Error in regenerate:', error);
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      <ToastContainer />

      {/* Simplified Header */}
      <header className="bg-white border-b border-gray-200 px-4 h-14 flex items-center justify-between shadow-sm md:flex hidden">
        <div className="flex items-center">
          <button
            onClick={() => setSidebarOpen(!isSidebarOpen)}
            className="p-2 hover:bg-gray-100 rounded-md"
          >
            <Menu size={20} />
          </button>
          <div
            className="flex items-center ml-3 cursor-pointer"
            onClick={handleNewConversation}
          >
            <img src="/images/lzct_logo_1.png" alt="柳州文旅 AI" className="h-10" />
          </div>
        </div>

        {/* User Profile */}
        <div
          className="relative"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center cursor-pointer">
            {userName ? userName.charAt(0).toUpperCase() : "U"}
          </div>

          {dropdownVisible && (
            <>
              {/* 添加一个不可见的连接区域 */}
              <div className="absolute w-full h-2 bottom-0 translate-y-full" />

              <div
                ref={dropdownRef}
                className="absolute right-0 top-full mt-2 w-32 bg-white rounded-md shadow-lg z-50"
              >
                <div className="py-1">
                  <button
                    onClick={handleChangePassword}
                    className="w-full px-2 py-2 text-left text-sm hover:bg-gray-100 flex items-center whitespace-nowrap"
                  >
                    <Key size={14} className="mr-2" />
                    修改密码
                  </button>
                  <button
                    onClick={handleLogout}
                    className="w-full px-2 py-2 text-left text-sm hover:bg-gray-100 flex items-center whitespace-nowrap"
                  >
                    <LogOut size={14} className="mr-2" />
                    退出登录
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </header>

      {/* Main Content Area - 添加响应式内边距 */}
      <div className="flex flex-1 overflow-hidden bg-gray-100 md:p-4 p-0">
        {/* Sidebar */}
        {isSidebarOpen && (
          <aside className="hidden md:block w-64 bg-white rounded-lg shadow-sm mr-4 overflow-hidden flex flex-col h-full">
            <div className="flex flex-col h-full">
              {/* 上部分区域：新建对话按钮和历史记录 */}
              <div className="flex-grow overflow-hidden flex flex-col">
                {/* 新建对话按钮区域 */}
                <div className="p-4">
                  <button
                    onClick={handleNewConversation}
                    className="w-full px-4 py-2 bg-gray-100 rounded-md text-sm flex items-center justify-center"
                  >
                    <Plus size={16} className="mr-2" />
                    新的对话
                  </button>
                </div>

                {/* 历史对话区域 */}
                {conversations.length > 0 && (
                  <div className="px-4 overflow-y-auto" style={{ height: 'calc(100% - 80px)' }}>
                    <h3 className="px-2 text-xs font-medium text-gray-500 uppercase mb-2">对话历史</h3>
                    <div className="space-y-1">
                      {conversations.map((conv) => (
                        <div
                          key={conv.session_id}
                          className={`flex items-center px-3 py-2 text-xs rounded-lg cursor-pointer transition-colors duration-200 group ${sessionId === conv.session_id
                            ? 'bg-orange-100 text-orange-700'
                            : 'hover:bg-gray-100 text-gray-600'
                            }`}
                          onClick={() => handleSessionSelect(conv.session_id)}
                        >
                          <FileText size={14} className="mr-2 flex-shrink-0" />
                          <span className="truncate flex-1 mr-1">{conv.intent_title}</span>
                          <button
                            className="opacity-0 group-hover:opacity-100 flex-shrink-0 w-5 h-5 flex items-center justify-center hover:bg-gray-200 rounded transition-all duration-200"
                            onClick={(e) => handleDeleteConversation(e, conv.session_id)}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M3 6h18"></path>
                              <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                              <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* 管理功能区域 - 固定在底部 */}
              {isManager && (
                <div className="border-t border-gray-200 bg-white">
                  <div className="p-4">
                    <h3 className="px-2 mb-2 text-xs font-medium text-gray-500 uppercase">管理功能</h3>
                    <div className="space-y-2">
                      <button
                        onClick={() => navigate('/admin/users')}
                        className="w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 rounded-lg flex items-center transition-colors duration-200"
                      >
                        <Users size={16} className="mr-2 text-gray-500" />
                        人员管理
                      </button>
                      <button
                        onClick={() => setKnowledgeModalOpen(true)}
                        className="w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 rounded-lg flex items-center transition-colors duration-200"
                      >
                        <BookOpen size={16} className="mr-2 text-gray-500" />
                        知识库维护
                      </button>
                      <button
                        onClick={handleModelModalOpen}
                        className="w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 rounded-lg flex items-center transition-colors duration-200"
                      >
                        <Database size={16} className="mr-2 text-gray-500" />
                        模型切换
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </aside>
        )}

        {/* Chat Area - 添加响应式背景色和圆角 */}
        <main className="flex-1 flex flex-col bg-white md:rounded-lg md:shadow-sm overflow-hidden">
          <div className="flex-1 overflow-y-auto px-6 py-6">
            {messages.length === 0 ? (
              <div className="flex flex-col items-center justify-between min-h-full max-w-3xl mx-auto">
                <div className="w-full flex flex-col items-center">
                  {/* 资料库 TAB 组 */}
                  <div className="w-[280px] mb-6">
                    <div className="flex w-full bg-white/80 backdrop-blur-sm p-1.5 rounded-full shadow-[0_2px_8px_rgba(0,0,0,0.08)]">
                      {resourceLibraries
                        .sort((a, b) => Number(b.enabled) - Number(a.enabled))
                        .map((library, index, array) => (
                          <React.Fragment key={library.id}>
                            <button
                              onClick={() => {
                                if (messages.length > 0) {
                                  toast.info('当前对话已经开始，如需切换资料库请开启新对话');
                                  return;
                                }
                                library.enabled && setActiveLibrary(library.id);
                              }}
                              disabled={!library.enabled || messages.length > 0}
                              className={`flex-1 inline-flex items-center justify-center px-4 py-2.5 rounded-full transition-all duration-200 text-sm ${activeLibrary === library.id
                                ? 'text-orange-500'
                                : !library.enabled || messages.length > 0
                                  ? 'text-gray-300 cursor-not-allowed hover:bg-transparent'
                                  : 'text-gray-600 hover:bg-gray-50/80'
                                }`}
                            >
                              <span className="mr-2">{library.icon}</span>
                              <span className="font-medium">{library.title}</span>
                            </button>
                            {index < array.length - 1 && (
                              <div className="w-px h-5 bg-gray-200 self-center mx-1" />
                            )}
                          </React.Fragment>
                        ))}
                    </div>
                  </div>

                  {/* Logo 和标题 */}
                  <div className="mb-8 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <div className="inline-flex items-center justify-center w-16 h-16">
                        <img src="/images/lzct_logo.png" alt="柳州文旅集团" className="w-16 h-16" />
                      </div>
                      <span className="text-3xl md:text-4xl font-semibold text-gray-800" style={{ fontFamily: 'SimSun, serif' }}>
                        文旅内控助手
                      </span>
                    </div>
                  </div>

                  {/* 消息输入框 */}
                  <div className="w-full max-w-4xl mx-auto mb-8">
                    <div className="bg-white rounded-2xl p-6 border-2 border-orange-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_16px_40px_rgb(0,0,0,0.06)] transition-shadow duration-300">
                      <div className="flex flex-col mb-5">
                        <div className="flex items-center mb-2">
                          <span className="text-orange-500 mx-1 md:mx-2 text-sm md:text-xl">🔥</span>
                          <span className="text-sm md:text-base text-gray-600 font-medium">内控合规智能大模型·专家级问答·定制化解决方案</span>
                        </div>
                      </div>
                      <div className="bg-gray-50/70 rounded-xl p-3 backdrop-blur-sm">
                        <textarea
                          className="w-full p-4 border-0 outline-none text-gray-700 resize-none bg-transparent text-base"
                          placeholder="请详细描述业务场景、具体金额和流程，以获得更准确的答案。例如：采购30万的仓储物流项目需要走什么流程？"
                          rows="3"
                          value={inputValue}
                          onChange={(e) => setInputValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleSend();
                            }
                          }}
                          disabled={isLoading}
                        />
                        <div className="flex justify-end mt-3 px-3">
                          <button
                            className={`px-6 py-2.5 rounded-full text-base flex items-center gap-2 font-medium transition-all duration-200 ${isLoading || !inputValue.trim()
                              ? 'bg-gray-200 text-gray-500'
                              : 'bg-gradient-to-r from-orange-500 to-orange-400 text-white hover:shadow-lg hover:shadow-orange-100 active:scale-95'
                              }`}
                            onClick={handleSend}
                            disabled={isLoading || !inputValue.trim()}
                          >
                            <Send size={18} />
                            <span>发送</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 推荐问题 */}
                  <div className="w-full max-w-4xl mx-auto">
                    <h2 className="text-base font-medium text-gray-900 mb-3">推荐问题</h2>
                    <div className="grid grid-cols-2 gap-3">
                      {trendingQuestions.map((question, index) => (
                        <div
                          key={index}
                          className="group relative bg-gradient-to-br from-white to-gray-50/80 rounded-lg p-3 h-[80px] cursor-pointer transition-all duration-300 hover:scale-[1.02] border border-gray-100/60 backdrop-blur-sm shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_16px_rgba(0,0,0,0.06)] overflow-hidden"
                          onClick={() => handleTrendingQuestionClick(question)}
                        >
                          {/* Wave background element */}
                          <div className="absolute -left-6 -top-6 w-16 h-16 opacity-15">
                            <svg
                              viewBox="0 0 200 200"
                              xmlns="http://www.w3.org/2000/svg"
                              className="w-full h-full text-orange-500"
                            >
                              <path
                                fill="currentColor"
                                d="M45.3,-76.3C59.9,-69.8,73.5,-59.4,80.7,-45.6C87.9,-31.8,88.7,-14.5,86.9,2.2C85.1,18.9,80.7,35.1,71.3,47.2C61.9,59.3,47.4,67.3,32.8,71.2C18.1,75,3.3,74.7,-12.4,73.1C-28,71.6,-44.6,68.9,-56.9,60.1C-69.2,51.3,-77.3,36.5,-80.9,20.5C-84.5,4.5,-83.7,-12.5,-77.7,-26.8C-71.7,-41,-60.6,-52.5,-47.6,-59.7C-34.6,-66.9,-19.8,-69.9,-3.6,-64.1C12.5,-58.3,30.6,-82.8,45.3,-76.3Z"
                                transform="translate(100 100)"
                              />
                            </svg>
                          </div>

                          {/* Hover gradient overlay */}
                          <div className="absolute inset-0 bg-gradient-to-br from-orange-500/[0.08] to-transparent rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                          {/* Content */}
                          <div className="relative z-10 h-full flex flex-col justify-between">
                            <p className="text-sm text-gray-700 line-clamp-2">{question.text}</p>
                            <div className="flex justify-end">
                              <span className="text-[10px] text-orange-500 bg-white/90 px-2 py-0.5 rounded-full shadow-sm border border-orange-100/60 group-hover:bg-orange-50 transition-colors duration-200">
                                去咨询
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Footer - 现在会固定在底部 */}
                <footer className="w-full py-1 text-center text-xs text-gray-400">
                  <p className="mb-1">© 柳州文旅集团内控智能助手</p>
                  <p>相关信息仅供内部学习参考，切勿引用为正式意见。</p>
                </footer>
              </div>
            ) : (
              <div className="overflow-y-auto mb-6">
                <div className="p-3 md:p-6 mt-4">
                  {messages.map((message, index) => (
                    <div key={message.id} className={`flex items-start gap-2 md:gap-3 mb-6 ${message.type === 'user' ? 'justify-end' : 'justify-start'
                      }`}>
                      {message.type === 'ai' && (
                        <div className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-orange-500 flex items-center justify-center text-white flex-shrink-0 text-xs md:text-sm">
                          AI
                        </div>
                      )}

                      <div className={`${message.type === 'user' ? 'max-w-[75%] md:max-w-[50%]' : 'max-w-[90%] md:max-w-[85%]'} px-3 md:px-4 py-2 md:py-3 rounded-xl ${message.type === 'user'
                        ? 'text-gray-800'
                        : 'bg-gray-100 text-gray-800 rounded-tl-none'
                        }`}>
                        {/* 添加推理过程展示 */}
                        {message.type === 'ai' && message.thoughts && message.thoughts.length > 0 && message.thoughts[0].thought && (
                          <div className="mb-2 text-sm">
                            <div className="pl-3 border-l-2 border-orange-300 text-gray-600 italic bg-orange-50/50 py-1.5 px-2 rounded whitespace-pre-wrap">
                              {message.thoughts[0].thought}
                            </div>
                          </div>
                        )}
                        <MessageContent
                          message={{
                            ...message,
                            onRegenerate: message.type === 'ai' && !isViewingHistory ? () => handleRegenerate(index) : undefined
                          }}
                          versions={message.versions}
                          isLatest={index === messages.length - 1}
                        />
                      </div>

                      {message.type === 'user' && (
                        <div className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-700 flex-shrink-0 text-xs md:text-sm">
                          {userName ? userName.charAt(0).toUpperCase() : "U"}
                        </div>
                      )}
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              </div>
            )}
          </div>

          {messages.length > 0 && (
            <div className="border-t border-gray-100 bg-white/80 backdrop-blur-sm">
              <div className="max-w-4xl mx-auto px-4">
                {!isViewingHistory && (
                  <div className="relative py-4">
                    {/* 主输入框区域 */}
                    <div className="relative bg-gray-50 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.08)] border border-gray-100/60">
                      <input
                        type="text"
                        className="w-full h-12 pl-5 pr-24 border-0 outline-none text-gray-700 bg-transparent placeholder:text-gray-400 text-sm"
                        placeholder="继续提问..."
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleSend();
                          }
                        }}
                        disabled={isLoading}
                      />

                      {/* 发送按钮 - 绝对定位在输入框内 */}
                      <div className="absolute right-1.5 top-1.5 bottom-1.5">
                        <button
                          className={`h-9 px-4 rounded-lg text-sm flex items-center gap-1.5 transition-all duration-200 ${isLoading || !inputValue.trim()
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-gradient-to-r from-orange-500 to-orange-400 text-white hover:shadow-sm hover:shadow-orange-100/50 active:scale-95'
                            }`}
                          onClick={handleSend}
                          disabled={isLoading || !inputValue.trim()}
                        >
                          <Send size={14} className={isLoading ? 'animate-pulse' : ''} />
                          <span className="font-medium">{isLoading ? '发送中' : '发送'}</span>
                        </button>
                      </div>
                    </div>

                    {/* Footer */}
                    <footer className="absolute left-0 right-0 bottom-0 transform translate-y-full pt-2 pb-1">
                      <p className="text-center text-xs text-gray-400 mb-1">© 柳州文旅集团内控智能助手</p>
                      <p className="text-center text-xs text-gray-400">相关信息仅供内部学习参考，切勿引用为正式意见。</p>
                    </footer>
                  </div>
                )}
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Delete Confirmation Dialog */}
      {deleteDialog.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-4 transform transition-all">
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-orange-100 mx-auto mb-4 flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-orange-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">确认删除？</h3>
              <div className="text-sm text-gray-500 mb-2 px-4 py-2 bg-gray-50 rounded-lg mx-auto max-w-fit">
                {conversations.find(conv => conv.session_id === deleteDialog.sessionId)?.intent_title || '未命名对话'}
              </div>
              <p className="text-sm text-red-500 mb-6">
                删除后将无法恢复此对话的内容，是否确认删除？
              </p>
            </div>
            <div className="flex justify-center gap-3">
              <button
                onClick={() => setDeleteDialog({ isOpen: false, sessionId: null })}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
              >
                取消
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 transition-colors duration-200"
              >
                删除
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password Modal */}
      <ChangePasswordModal
        isOpen={isPasswordModalOpen}
        onClose={() => setPasswordModalOpen(false)}
      />

      {/* 添加模型切换弹出层 */}
      {isModelModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4 transform transition-all">
            <div className="mb-4">
              <h3 className="text-lg font-medium text-gray-900">选择模型</h3>
              <p className="text-sm text-gray-500 mt-1">
                请选择要使用的大语言模型
              </p>
              {/* 添加提示信息 */}
              <div className="mt-2 p-2 bg-orange-50 border border-orange-100 rounded-md">
                <p className="text-xs text-orange-600 flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  模型切换仅针对集团制度库生效，且对所有用户统一生效
                </p>
              </div>
            </div>

            <div className="space-y-2">
              {[
                {
                  id: 'default',
                  name: '默认模型',
                  description: '集团制度库的问答默认使用此模型。'
                },
                {
                  id: 'r1-32b',
                  name: 'DeepSeek R1',
                  description: 'DeepSeek R1 在后训练阶段大规模使用了强化学习技术，极大提升了模型推理能力。在自然语言推理等任务上能力较强。'
                }
              ].map((model) => (
                <div
                  key={model.id}
                  onClick={() => handleModelSelect(model.id)}
                  className={`p-4 rounded-lg cursor-pointer transition-all duration-200 ${(tempSelectedModel || currentModel) === model.id
                    ? 'bg-orange-50 border-2 border-orange-500'
                    : 'border-2 border-gray-100 hover:border-gray-200'
                    }`}
                >
                  <div className="flex">
                    <div className={`w-4 h-4 rounded-full border-2 mr-3 flex-shrink-0 mt-1 ${(tempSelectedModel || currentModel) === model.id
                      ? 'border-orange-500 bg-orange-500'
                      : 'border-gray-300'
                      }`}>
                      {(tempSelectedModel || currentModel) === model.id && (
                        <div className="w-2 h-2 bg-white rounded-full m-0.5" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-gray-900">{model.name}</h4>
                      <p className="text-xs text-gray-500 mt-0.5 break-words leading-relaxed">
                        {model.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => {
                  setTempSelectedModel(null);
                  setModelModalOpen(false);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
              >
                取消
              </button>
              <button
                onClick={handleConfirmModelChange}
                disabled={!tempSelectedModel}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${tempSelectedModel
                  ? 'bg-orange-500 text-white hover:bg-orange-600'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
              >
                确认
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Knowledge Modal */}
      <KnowledgeModal
        isOpen={isKnowledgeModalOpen}
        onClose={() => setKnowledgeModalOpen(false)}
      />
    </div>
  );
};

export default OptimizedChatInterface;